import React from 'react';
import { User } from '../../../../contexts/LicenseManagementContext/LicenseManagementContext.types';
import { useUser } from '../../../../hooks/useUser';
import { useAppConfigContext } from '../../../../contexts/AppConfigProvider';
import { IApiConfig, useApi } from '../../../../hooks/useApi';

export const useUserDeletion = (props: {
    onDeleteSuccess: () => void;
    onDeleteFailure: () => void;
}) => {
    const { onDeleteFailure, onDeleteSuccess } = props;
    const [deleteInitiated, setDeleteInitiated] = React.useState(false);

    const { api } = useAppConfigContext();
    const invokingUser = useUser();

    const [deleteOptions, setDeleteOptions] = React.useState<
        | {
              user: User | undefined;
          }
        | undefined
    >(undefined);

    const deleteUser = React.useMemo(
        () => (props: { user: User }) => {
            setDeleteOptions({
                user: props.user,
            });
            setDeleteInitiated(true);
        },
        [],
    );

    const deleteUserApiConfig = React.useMemo<IApiConfig>(
        () => ({
            url: `${api!.userManagement}/v2/tenants/${invokingUser?.tenantIdentifier}/users/${encodeURIComponent(deleteOptions?.user?.userId || '')}`,
            useAuth: true,
            executeType: 'manual',
            method: 'DELETE',
        }),
        [api, invokingUser, deleteOptions],
    );

    const { data, error, loading, execute, reset } =
        useApi(deleteUserApiConfig);

    React.useEffect(() => {
        if (deleteInitiated) {
            execute();
        }
    }, [execute, deleteInitiated]);

    React.useEffect(() => {
        if (error && !loading) {
            onDeleteFailure();
            reset();
            setDeleteInitiated(false);
            setDeleteOptions(undefined);
        }
    }, [error, loading, onDeleteFailure, reset]);

    React.useEffect(() => {
        if (data && !loading && !error) {
            onDeleteSuccess();
            reset();
            setDeleteInitiated(false);
            setDeleteOptions(undefined);
        }
    }, [data, error, loading, onDeleteSuccess, reset]);

    return {
        deleteUser,
        deleteInProgress: deleteInitiated,
    };
};
